/**
 * Werktijden-afwijking — vaste reden-opties (dropdown) + toelichting-regel.
 *
 * Gedeeld tussen portal-UI (de dropdown in de werkbon-dagkaart) en de API
 * (server-side validatie). Eén bron, zodat client en server niet uit elkaar
 * lopen — analoog aan REIS_AFWIJKING_TYPES voor de reis-afwijking.
 *
 * De gekozen reden wordt opgeslagen in `WerkbonDayEntry.afwijkingReden`; een
 * optionele vrije-tekst toelichting in `WerkbonDayEntry.afwijkingToelichting`.
 * De toelichting is altijd optioneel, behalve wanneer de reden 'Anders' is —
 * dan is een korte uitleg verplicht (anders zegt 'Anders' niets).
 */

/** Reden-waarde die een verplichte toelichting afdwingt. */
export const UREN_AFWIJKING_REDEN_ANDERS = 'Anders' as const

/** Reden-opties bij een afwezige (niet-gewerkte) dag. */
export const UREN_AFWIJKING_REDENEN_AFWEZIG = [
  'Ziek',
  'Verlof / vrij',
  'ATV / TVT',
  'Geen werk beschikbaar',
  UREN_AFWIJKING_REDEN_ANDERS,
] as const

/** Reden-opties bij afwijkende uren (langer of korter dan gepland). */
export const UREN_AFWIJKING_REDENEN_AFWIJKEND = [
  'Langer doorgewerkt (uitloop)',
  'Storing / extra werk',
  'Wachttijd op locatie',
  'Eerder klaar / minder werk',
  UREN_AFWIJKING_REDEN_ANDERS,
] as const

export type UrenAfwijkingRedenAfwezig = (typeof UREN_AFWIJKING_REDENEN_AFWEZIG)[number]
export type UrenAfwijkingRedenAfwijkend = (typeof UREN_AFWIJKING_REDENEN_AFWIJKEND)[number]

/** Alle toegestane reden-waarden (union, gededupliceerd). */
export const ALLE_UREN_AFWIJKING_REDENEN: readonly string[] = Array.from(
  new Set<string>([
    ...UREN_AFWIJKING_REDENEN_AFWEZIG,
    ...UREN_AFWIJKING_REDENEN_AFWIJKEND,
  ]),
)

/** Geeft de juiste reden-lijst voor de situatie (afwezig vs afwijkende uren). */
export function urenAfwijkingRedenOpties(isAfwezig: boolean): readonly string[] {
  return isAfwezig ? UREN_AFWIJKING_REDENEN_AFWEZIG : UREN_AFWIJKING_REDENEN_AFWIJKEND
}

/** True wanneer de gekozen reden een vrije-tekst toelichting vereist ('Anders'). */
export function urenAfwijkingVereistToelichting(reden: string | null | undefined): boolean {
  return (reden ?? '').trim() === UREN_AFWIJKING_REDEN_ANDERS
}

/**
 * Mapt een afwezig-reden-label (uit `UREN_AFWIJKING_REDENEN_AFWEZIG`) naar de
 * gestructureerde DayEntryAfwezigReden-enum (ziekte/vrij/overig). De portal stuurt
 * bij een afwezige dag naast de vrije reden ook deze structurele enum mee, zodat
 * gereedmelden/weergave/PDF de afwezigheid uniform kunnen behandelen. Return-type
 * is de literal union die overeenkomt met `DayEntryAfwezigReden`.
 */
export function afwezigRedenEnumVanLabel(
  label: string | null | undefined,
): 'ZIEKTE' | 'VRIJ' | 'OVERIG' {
  const v = (label ?? '').trim().toLowerCase()
  if (v === 'ziek') return 'ZIEKTE'
  if (v === 'verlof / vrij' || v === 'atv / tvt') return 'VRIJ'
  return 'OVERIG'
}
